import { Icon } from "@aurem/components";
import { useBoolean } from "@aurem/ui";
import { companyProducts } from "const/routes";
import { useAuthorize } from "graphql/user";
import { useNavigation } from "hooks/navigate";
import { useBasePath } from "hooks/useBasePath";
import {
  ClipboardCheckIcon,
  HouseIcon,
  MenuIcon,
  Package,
  Users,
} from "lucide-react";
import { useFeature } from "providers/FeaturesProvider";
import { useI18n } from "providers/I18nProvider";
import { useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { PermissionEnum } from "types";
import { BottomNavigationBar } from "../BottomNavigationBar";
import { BottomNavigationButton } from "../BottomNavigationButton";
import { BottomNavigationItem } from "../types";
import { MoreDrawer } from "./MoreDrawer";

export const CompanyBottomNavigation = () => {
  const { t } = useI18n();
  const navigate = useNavigation();
  const { path } = useBasePath();
  const { pathname } = useLocation();
  const { companyId = "" } = useParams();

  const {
    value: showMoreDrawer,
    setTrue: openMoreDrawer,
    setFalse: closeMoreDrawer,
  } = useBoolean(false);

  const isTasksEnabled = useFeature("tasks_feature");
  const canViewDashboard = useAuthorize(
    PermissionEnum.ViewCompanyDashboard,
    companyId,
  );
  const canListEmployees = useAuthorize(PermissionEnum.ListEmployee, companyId);
  const canViewPayments = useAuthorize(
    PermissionEnum.ViewCompanyPayment,
    companyId,
  );
  const canViewBenefits = useAuthorize(PermissionEnum.ViewBenefit, companyId);

  const items = useMemo((): BottomNavigationItem[] => {
    const basePath = `companies/${companyId}`;

    const allItems: (BottomNavigationItem & { show: boolean })[] = [
      {
        id: "dashboard",
        text: t("apps.admin.common.dashboard"),
        icon: <HouseIcon size="1.25rem" className="rtl:-scale-x-100" />,
        onClick: () => navigate(path(`${basePath}/dashboard/overview`)),
        active: pathname.includes("/dashboard"),
        show: canViewDashboard,
      },
      {
        id: "tasks",
        text: t("apps.admin.navBar.tasks"),
        icon: (
          <ClipboardCheckIcon size="1.25rem" className="rtl:-scale-x-100" />
        ),
        onClick: () => navigate(path(`${basePath}/tasks`)),
        active: pathname.includes("/tasks"),
        show: isTasksEnabled,
      },
      {
        id: "employees",
        text: t("apps.admin.common.employees"),
        icon: <Users size="1.25rem" className="rtl:-scale-x-100" />,
        onClick: () => navigate(path(`${basePath}/employees`)),
        active: pathname.includes("/employees"),
        show: canListEmployees,
      },
      {
        id: "products",
        text: t("apps.admin.pages.companies.companyId.products.page.title"),
        icon: <Package size="1.25rem" className="rtl:-scale-x-100" />,
        onClick: () => navigate(companyProducts({ companyId })),
        active: pathname.includes("/products"),
        show: canViewBenefits,
      },
      {
        id: "payments",
        text: t("apps.admin.common.payments"),
        icon: <Icon.Payment size="1.25rem" className="rtl:-scale-x-100" />,
        onClick: () => navigate(path(`${basePath}/payments`)),
        active: pathname.includes("/payments"),
        show: canViewPayments,
      },
      {
        id: "menu",
        text: t("apps.employee.navigation.menu"),
        icon: <MenuIcon size="1.25rem" className="rtl:-scale-x-100" />,
        onClick: openMoreDrawer,
        active: showMoreDrawer,
        show: true,
      },
    ];

    return allItems.filter((item) => item.show);
  }, [
    companyId,
    navigate,
    path,
    pathname,
    t,
    isTasksEnabled,
    canViewDashboard,
    canListEmployees,
    canViewBenefits,
    canViewPayments,
    openMoreDrawer,
    showMoreDrawer,
  ]);

  const buttons = items.map((item) => {
    return <BottomNavigationButton key={item.id} item={item} />;
  });

  return (
    <>
      <BottomNavigationBar>{buttons}</BottomNavigationBar>
      <MoreDrawer open={showMoreDrawer} onClose={closeMoreDrawer} />
    </>
  );
};
